// Sessiyalar — xsib-core `session` moduli bilan haqiqiy kontrakt.
// Manba: `session/internal/SessionController.java`.
//
// ⚠️ TRAFIK YO'NALISHI NAS NUQTAI NAZARIDAN (CLAUDE.md §6):
//   inputOctets  = abonent YUBORGAN (upload)
//   outputOctets = abonent OLGAN    (download)
// Ularni almashtirib qo'yish oson va xato faqat hisobotda ko'rinadi,
// shuning uchun quyida `upload`/`download` deb ATAYLAB qayta nomlangan.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

const activeSessionSchema = z.object({
  radacctId: z.number(),
  username: z.string(),
  framedIp: z.string().nullish(),
  callingStationId: z.string().nullish(),
  nasIp: z.string().nullish(),
  startTime: instantSchema.nullish(),
  lastUpdate: instantSchema.nullish(),
  sessionTime: z.number(),
  inputOctets: z.number(),
  outputOctets: z.number(),
  acctSessionId: z.string().nullish(),
  interimInterval: z.number().nullish(),
})

export type ActiveSessionRaw = z.infer<typeof activeSessionSchema>

export interface ActiveSession {
  radacctId: number
  username: string
  framedIp: string | null
  callingStationId: string | null
  nasIp: string | null
  startTime: string | null
  lastUpdate: string | null
  sessionTime: number
  /** Abonent YUBORGAN (`acctinputoctets`) */
  uploadBytes: number
  /** Abonent OLGAN (`acctoutputoctets`) */
  downloadBytes: number
  acctSessionId: string | null
  interimInterval: number | null
}

function toActiveSession(raw: ActiveSessionRaw): ActiveSession {
  return {
    radacctId: raw.radacctId,
    username: raw.username,
    framedIp: raw.framedIp ?? null,
    callingStationId: raw.callingStationId ?? null,
    nasIp: raw.nasIp ?? null,
    startTime: raw.startTime ?? null,
    lastUpdate: raw.lastUpdate ?? null,
    sessionTime: raw.sessionTime,
    uploadBytes: raw.inputOctets,
    downloadBytes: raw.outputOctets,
    acctSessionId: raw.acctSessionId ?? null,
    interimInterval: raw.interimInterval ?? null,
  }
}

const sessionHistorySchema = z.object({
  radacctId: z.number(),
  username: z.string(),
  framedIp: z.string().nullish(),
  callingStationId: z.string().nullish(),
  nasIp: z.string().nullish(),
  startTime: instantSchema.nullish(),
  stopTime: instantSchema.nullish(),
  sessionTime: z.number(),
  inputOctets: z.number(),
  outputOctets: z.number(),
  acctSessionId: z.string().nullish(),
  terminateCause: z.string().nullish(),
  /** Backend `isOpen()` ni `open` deb serializatsiya qiladi */
  open: z.boolean().nullish(),
})

export interface SessionHistoryRow {
  radacctId: number
  username: string
  framedIp: string | null
  callingStationId: string | null
  nasIp: string | null
  startTime: string | null
  stopTime: string | null
  sessionTime: number
  uploadBytes: number
  downloadBytes: number
  acctSessionId: string | null
  terminateCause: string | null
  open: boolean
}

/**
 * `session.api.SessionPage` — `{rows, total, page, size, hasNext}`.
 *
 * ⚠️ MAYDON NOMI `rows`, `items` EMAS. Bu yerda avval `items` yozilgan edi
 * (backend tipining ESKI shakli) va natijada zod HAR javobni rad etardi:
 * `DataTable` doim `isPending` holatida qolib, tarix bo'limida jadval
 * o'rniga mangu skeleton ko'rinardi — xato xabari ham chiqmasdi, chunki
 * react-query qayta urinishlar orasida hali "pending" hisoblanadi.
 * `subscriber.api.SubscriberPage` bilan atayin bir xil nom qilingan.
 *
 * ⚠️ `total` MAVJUD, lekin qiymati deyarli doim `null`: `radacct` ustida
 * `COUNT(*)` qimmat. "Maydon yo'q" va "qiymat noma'lum" boshqa-boshqa narsa
 * — shuning uchun `nullish`, `optional` emas. Navigatsiya `hasNext` ga
 * tayanadi (quyida).
 */
const sessionPageSchema = z.object({
  rows: z.array(sessionHistorySchema),
  total: z.number().nullish(),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
  empty: z.boolean().nullish(),
})

export async function fetchActiveSessions(limit = 200): Promise<ActiveSession[]> {
  const response = await api.get<unknown>('/api/sessions/active', { params: { limit } })
  return z.array(activeSessionSchema).parse(response.data).map(toActiveSession)
}

/** Bitta abonentning barcha faol sessiyalari (ko'p qurilma bo'lishi mumkin) */
export async function fetchActiveSessionsOf(username: string): Promise<ActiveSession[]> {
  const response = await api.get<unknown>(`/api/sessions/active/${encodeURIComponent(username)}`)
  return z.array(activeSessionSchema).parse(response.data).map(toActiveSession)
}

export interface HistoryFilter {
  username?: string | undefined
  /** ISO-8601 UTC */
  from?: string | undefined
  to?: string | undefined
}

/**
 * Sessiyalar tarixi.
 *
 * `total` BO'LMAGANI uchun uni taxmin qilamiz: `hasNext` bo'lsa "yana bitta
 * to'liq sahifa bor" deb ko'rsatamiz. Bu son PAGINATSIYA TUGMALARI uchun
 * ishlatiladi, foydalanuvchiga "jami" sifatida KO'RSATILMAYDI (jadval
 * `showTotal` ni tarixda o'chiradi) — aks holda panel bilmagan narsasini
 * bilgandek qilib ko'rsatardi.
 */
export async function fetchSessionHistory(
  state: TableState,
  signal: AbortSignal,
  filter: HistoryFilter = {},
): Promise<PageResult<SessionHistoryRow>> {
  const username = filter.username ?? (state.search.trim() || undefined)

  const response = await api.get<unknown>('/api/sessions/history', {
    signal,
    params: {
      page: state.page - 1,
      size: state.pageSize,
      ...(username ? { username } : {}),
      ...(filter.from ? { from: filter.from } : {}),
      ...(filter.to ? { to: filter.to } : {}),
    },
  })

  const parsed = sessionPageSchema.parse(response.data)
  const items: SessionHistoryRow[] = parsed.rows.map((row) => ({
    radacctId: row.radacctId,
    username: row.username,
    framedIp: row.framedIp ?? null,
    callingStationId: row.callingStationId ?? null,
    nasIp: row.nasIp ?? null,
    startTime: row.startTime ?? null,
    stopTime: row.stopTime ?? null,
    sessionTime: row.sessionTime,
    uploadBytes: row.inputOctets,
    downloadBytes: row.outputOctets,
    acctSessionId: row.acctSessionId ?? null,
    terminateCause: row.terminateCause ?? null,
    open: row.open ?? row.stopTime === null,
  }))

  // Server jami sonni hisoblagan bo'lsa — o'shani ishlatamiz; aks holda
  // sahifalash tugmalari uchun taxmin (foydalanuvchiga "jami" deb
  // KO'RSATILMAYDI, `showTotal={false}`)
  const seenSoFar = (state.page - 1) * state.pageSize + items.length
  const total = parsed.total ?? (parsed.hasNext ? seenSoFar + state.pageSize : seenSoFar)
  return { items, total }
}

// --- Autentifikatsiya jurnali (`radpostauth`) -------------------------------
//
// ⚠️ PAROL BU YERDA YO'Q va bo'lmaydi. `radpostauth.pass` da abonent
// KIRITGAN parolning o'zi turadi (jonli bazada 389 qatordan 258 tasida
// to'ldirilgan). Backend uni DTO'ga ham, SQL'ga ham kiritmaydi
// (CLAUDE.md §7). Panel tomonda ham hech qachon so'ralmasin.
//
// ⚠️ NAS IP YO'Q. `radpostauth` sxemasida `nasipaddress` USTUNI UMUMAN
// YO'Q — bu jonli bazada tekshirilgan. Eng yaqin ma'lumot
// `calledstationid`: MikroTik u yerga XIZMAT nomini yozadi (o'lchangan
// qiymatlar: `isb-net`, `isb-hotspot`, `hotspot1`). Uni "NAS" deb
// ko'rsatish yo'q ma'lumotni bor deb ko'rsatish bo'lardi.

const authAttemptSchema = z.object({
  id: z.number(),
  username: z.string(),
  reply: z.string().nullish(),
  authDate: instantSchema.nullish(),
  calledStationId: z.string().nullish(),
  callingStationId: z.string().nullish(),
  /** Backend `isAccepted()` ni `accepted` deb seriyalaydi */
  accepted: z.boolean().nullish(),
})

const authLogPageSchema = z.object({
  rows: z.array(authAttemptSchema),
  total: z.number().nullish(),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
  empty: z.boolean().nullish(),
})

export interface AuthAttempt {
  id: number
  username: string
  reply: string | null
  authDate: string | null
  /** MikroTik xizmat nomi — NAS IP EMAS (yuqoridagi izoh) */
  calledStationId: string | null
  callingStationId: string | null
  accepted: boolean
}

export type AuthOutcome = 'ANY' | 'ACCEPTED' | 'REJECTED'

export interface AuthLogFilter {
  username?: string | undefined
  outcome?: AuthOutcome | undefined
  /** ISO-8601 UTC */
  from?: string | undefined
  to?: string | undefined
}

/** Bo'sh satr `radpostauth` da tez-tez uchraydi — u "ma'lumot yo'q" degani */
function blankToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null
  return value.trim() === '' ? null : value
}

function toAuthAttempt(raw: z.infer<typeof authAttemptSchema>): AuthAttempt {
  return {
    id: raw.id,
    username: raw.username,
    reply: raw.reply ?? null,
    authDate: raw.authDate ?? null,
    calledStationId: blankToNull(raw.calledStationId),
    callingStationId: blankToNull(raw.callingStationId),
    // Server bermasa matndan o'zimiz aniqlaymiz — panel serializatsiya
    // sozlamasi o'zgarganda "hammasi rad etildi" deb ko'rsatmasin
    accepted: raw.accepted ?? (raw.reply ?? '').startsWith('Access-Accept'),
  }
}

/**
 * Autentifikatsiya urinishlari — sahifalangan.
 *
 * `total` yo'q (`SessionPage`), shuning uchun sahifalash tugmalari uchun
 * `hasNext` dan taxmin qilinadi — tarix jadvalidagi bilan bir xil qoida,
 * va foydalanuvchiga "jami" deb KO'RSATILMAYDI.
 */
export async function fetchAuthLog(
  state: TableState,
  signal: AbortSignal,
  filter: AuthLogFilter = {},
): Promise<PageResult<AuthAttempt>> {
  const username = filter.username ?? (state.search.trim() || undefined)

  const response = await api.get<unknown>('/api/sessions/auth-log', {
    signal,
    params: {
      page: state.page - 1,
      size: state.pageSize,
      ...(username ? { username } : {}),
      ...(filter.outcome && filter.outcome !== 'ANY' ? { outcome: filter.outcome } : {}),
      ...(filter.from ? { from: filter.from } : {}),
      ...(filter.to ? { to: filter.to } : {}),
    },
  })

  const parsed = authLogPageSchema.parse(response.data)
  const items = parsed.rows.map(toAuthAttempt)
  const seenSoFar = (state.page - 1) * state.pageSize + items.length
  const total = parsed.total ?? (parsed.hasNext ? seenSoFar + state.pageSize : seenSoFar)
  return { items, total }
}

const statsSchema = z.object({
  activeSessions: z.number(),
  onlineSubscribers: z.number(),
})

export type SessionStats = z.infer<typeof statsSchema>

export async function fetchSessionStats(): Promise<SessionStats> {
  const response = await api.get<unknown>('/api/sessions/stats')
  return statsSchema.parse(response.data)
}

/**
 * Backend `Duration` ni ISO-8601 shaklida beradi ("PT2H30M"), lekin Jackson
 * sozlamasiga qarab soniya (son) bo'lishi ham mumkin. Ikkalasi ham qabul
 * qilinadi — panel serializatsiya sozlamasi o'zgarganda yiqilmasin.
 */
const durationSchema = z.union([z.string(), z.number()])

const staleSessionSchema = z.object({
  session: activeSessionSchema,
  silentFor: durationSchema.nullish(),
  threshold: durationSchema.nullish(),
  closeThreshold: durationSchema.nullish(),
})

export interface StaleSession {
  session: ActiveSession
  /** Soniyada; aniqlab bo'lmasa `null` */
  silentForSeconds: number | null
  /**
   * Tizim bu sessiyani MAJBURAN yopadigan chegara (soniyada).
   *
   * ⚠️ Panel buni ko'rsatishi shart. Rekonsiliatsiya 2026-08-07 dan
   * boshlab sessiyalarni o'zi yopadi (QARORLAR §17-A) — operator
   * yozuv o'zgarishini kutmoqda ekanini BILISHI kerak, aks holda
   * yopilgan sessiya uning uchun tushunarsiz yo'qolish bo'lardi.
   */
  closeThresholdSeconds: number | null
}

/** ISO-8601 davomiylik ("PT2H30M") yoki soniya → soniya */
function toSeconds(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return value
  const match = /^PT(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?$/.exec(value)
  if (!match) return null
  const [, h, m, s] = match
  return Number(h ?? 0) * 3600 + Number(m ?? 0) * 60 + Number(s ?? 0)
}

export async function fetchStaleSessions(): Promise<StaleSession[]> {
  const response = await api.get<unknown>('/api/sessions/stale')
  return z
    .array(staleSessionSchema)
    .parse(response.data)
    .map(toStaleSession)
}

function toStaleSession(raw: z.infer<typeof staleSessionSchema>): StaleSession {
  return {
    session: toActiveSession(raw.session),
    silentForSeconds: toSeconds(raw.silentFor),
    closeThresholdSeconds: toSeconds(raw.closeThreshold),
  }
}

const reconciliationSchema = z.object({
  /** `null` = job hali yurmagan. Bu BO'SH RO'YXATDAN boshqa ma'no */
  checkedAt: instantSchema.nullish(),
  activeSessions: z.number(),
  staleSessions: z.number(),
  stale: z.array(staleSessionSchema).default([]),
})

export interface ReconciliationStatus {
  checkedAt: string | null
  activeSessions: number
  staleSessions: number
  /** Tekshiruv umuman o'tkazilganmi — bo'sh ro'yxat "toza" degani EMAS */
  hasRun: boolean
}

export async function fetchReconciliation(): Promise<ReconciliationStatus> {
  const response = await api.get<unknown>('/api/sessions/reconciliation')
  const parsed = reconciliationSchema.parse(response.data)
  return {
    checkedAt: parsed.checkedAt ?? null,
    activeSessions: parsed.activeSessions,
    staleSessions: parsed.staleSessions,
    hasRun: parsed.checkedAt != null,
  }
}

// --- Boshqaruv --------------------------------------------------------------

/** Bitta sessiyani uzish. Bazani O'ZGARTIRMAYDI (CLAUDE.md §5.5). */
export async function disconnectSession(radacctId: number): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/sessions/${radacctId}/disconnect`)
  return operationResultSchema.parse(response.data)
}

/**
 * Osilib qolgan sessiyani BAZADA yopish.
 *
 * Routerda mavjud bo'lmagan sessiya uchun yagona yo'l — unga Disconnect
 * yuborib bo'lmaydi (NAK qaytaradi).
 */
export async function closeStaleSession(radacctId: number): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/sessions/${radacctId}/close-stale`)
  return operationResultSchema.parse(response.data)
}

/** Abonentning BARCHA qurilmalarini uzish */
export async function disconnectUser(username: string): Promise<OperationResult> {
  const response = await api.post<unknown>(
    `/api/sessions/user/${encodeURIComponent(username)}/disconnect`,
  )
  return operationResultSchema.parse(response.data)
}

/**
 * Yangi tezlikni FAOL sessiyalarga qo'llash.
 *
 * ⚠️ Bu endpoint doimiy holatni YOZMAYDI — u allaqachon yozilgan bo'lishi
 * kerak (tarif/siyosat orqali). Ya'ni bu "tarifni o'zgartirish" emas,
 * "o'zgargan tarifni hozir ulangan qurilmalarga yetkazish".
 */
export async function applyRateLimit(
  username: string,
  rateLimit: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(
    `/api/sessions/user/${encodeURIComponent(username)}/rate-limit`,
    { rateLimit },
  )
  return operationResultSchema.parse(response.data)
}

// --- Qurilmaga qo'llanmagan amallar ----------------------------------------
//
// Manba: `session/coa/resource/SessionControlResource#inconsistencies`,
// `session/coa/domain/CoaPending`.
//
// ⚠️ BU XATOLAR RO'YXATI EMAS. Har qator uchun baza TO'G'RI: abonent
// bloklangan yoki tarifi o'zgargan. Faqat faol sessiyaga qo'llanmagan.
// Shuning uchun panelda rangi SARIQ, qizil emas (CLAUDE.md §3.2,
// Qoida 4). Qizil ko'rsatish operatorni "tizim buzildi" degan xato
// xulosaga olib kelardi.

const coaPendingSchema = z.object({
  id: uuidSchema,
  username: z.string(),
  nasIp: z.string(),
  operation: z.enum(['RATE_LIMIT', 'DISCONNECT']),
  desiredValue: z.string().nullish(),
  attempts: z.number(),
  lastError: z.string().nullish(),
  firstSeenAt: instantSchema.nullish(),
  lastAttemptAt: instantSchema.nullish(),
})

export interface CoaPending {
  id: string
  username: string
  nasIp: string
  operation: 'RATE_LIMIT' | 'DISCONNECT'
  desiredValue: string | null
  /** ⚠️ Panelda ko'rsatiladi: "20 marta urinildi" va "hozir sezildi" — boshqa vaziyat */
  attempts: number
  lastError: string | null
  firstSeenAt: string | null
  lastAttemptAt: string | null
}

export async function fetchInconsistencies(): Promise<CoaPending[]> {
  const response = await api.get<unknown>('/api/sessions/inconsistencies')
  return z.array(coaPendingSchema).parse(response.data).map((raw) => ({
    id: raw.id,
    username: raw.username,
    nasIp: raw.nasIp,
    operation: raw.operation,
    desiredValue: raw.desiredValue ?? null,
    attempts: raw.attempts,
    lastError: raw.lastError ?? null,
    firstSeenAt: raw.firstSeenAt ?? null,
    lastAttemptAt: raw.lastAttemptAt ?? null,
  }))
}
